// Billing presets. "billingA4" recreates the current hard-coded billing print
// (backend helper/Invoice_types/Invoice_1.ts) so it can be edited in the builder.
import { text, field, hline, vline, box, pair, col, table, fixedBand, tableBand, a4, thermal, preset, qr } from "./helpers";

const W = 194; // A4 content width with 8 mm margins
const GREY = { borderColor: "#808080" };

export function billingA4() {
  return preset(
    "billing_invoice",
    "Billing Invoice (A4) - current format",
    a4(8),
    [
      fixedBand(
        "Header",
        50,
        [
          text(0, 0, W, 5, "GST INVOICE", { bold: true, fontSize: 9, align: "center" }),
          box(0, 5, W, 45, { borderWidth: 0.75, ...GREY }),
          text(2, 6, 95, 5, "{{ store.company }}", { bold: true, fontSize: 9 }),
          text(2, 11, 95, 10, "**Store Code:** {{ store.store_code }}\n{{ store.company_address }}"),
          text(2, 21, 95, 4.5, "**Phone:** {{ store.company_telephone1 }}"),
          text(99, 6, 93, 5, '**Customer Name: {{ COALESCE(TRIM(cmr_first_name & " " & cmr_last_name), "-") }}**', { valign: "bottom" }),
          text(99, 11, 93, 4.5, "**Customer No.:** {{ cmr_phone_number }}"),
          text(99, 16, 93, 4.5, "**Doctor Name:** DR {{ doctor_name | dash }}"),
          text(99, 21, 93, 4.5, "**REG.NO:** {{ store.company_city }}"),
          hline(0, 26, W, GREY),
          text(2, 27, 64, 12, "**GST NO.:** {{ store.company_gstn | dash }}\n**PAN :** {{ store.pan_number | dash }}\n**FSSAI NO :** {{ store.fssai_no | dash }}"),
          text(
            2,
            39,
            64,
            7.5,
            '**D.L. No.:** {{ COALESCE(dl_no_20_20b, "-") & IF(dl_no_21_21b, ", " & dl_no_21_21b, "") & IF(dl_no_21c, ", " & dl_no_21c, "") }}'
          ),
          text(2, 45.5, 64, 4.5, "**Email:** {{ store.company_email | dash }}"),
          vline(68, 26, 24, GREY),
          text(68, 28, 58, 8, "CASH MEMO\nORIGINAL", { bold: true, align: "center" }),
          text(70, 38, 56, 9, "**Invoice No** : {{ invoice_number }}\n**Date** : {{ created_date | date:'DD/MM/YYYY' }}"),
          vline(127, 26, 24, GREY),
          text(129, 27, 63, 20, "**GST NO.:** {{ cmr_gst | dash }}\n**PAN.:** {{ cmr_pan | dash }}\n**Address:** {{ cmr_area | dash }}"),
        ],
        { repeat: "top" }
      ),
      tableBand(
        "Items",
        table(
          "items",
          "items",
          0,
          W,
          [
            col("sno", "S.No", "SNO()", 3.5, { align: "center" }),
            col("mfg", "Mfg", "item_mfg_code", 6, { align: "center", format: "dash" }),
            col("hsn", "HSN", "item_hsn", 6.5, { align: "center", format: "dash" }),
            col("name", "Product Name", "item_name", 15.5),
            col("batch", "Batch", "item_batch_number", 7, { align: "center", format: "dash" }),
            col("exp", "EXP", "item_exp_date", 5, { align: "center", format: "date:MM/YY" }),
            col("pack", "Pack", "item_uom", 5.5, { align: "center", format: "dash" }),
            col("qty", "Qty", "item_quantity", 5, { align: "right" }),
            col("mrp", "MRP", "item_unit_price", 6, { align: "right", format: "fixed:2" }),
            col("taxable", "Taxable Amt", "item_price_wiithout_tax", 7, { align: "right", format: "fixed:2" }),
            col("gst", "GST (%)", "item_total_tax_percentage", 4, { align: "center" }),
            col("cgst", "CGST (Amt)", "item_tax_amount / 2", 6, { align: "right", format: "fixed:2" }),
            col("sgst", "SGST (Amt)", "item_tax_amount / 2", 6, { align: "right", format: "fixed:2" }),
            col("disc", "Disc (%)", "item_discount_percentage", 4.5, { align: "center", format: "fixed:2" }),
            col("amount", "Total Amount", "item_total_amount", 13, { align: "right", format: "fixed:2" }),
          ],
          {
            minRows: 13,
            style: { fontSize: 7.5, grid: "columns", borderWidth: 0.75, borderColor: "#000000", cellPadding: 0.8, rowHeight: 4.5 },
            headerStyle: { bold: true, bg: "#dbdbdb" },
          }
        )
      ),
      fixedBand("Totals", 62, [
        box(0, 0, W, 62, { borderWidth: 0.75, ...GREY }),
        text(5, 1, 66, 14, "Please consult your doctor before using medicine.\nE & O E"),
        vline(75, 0, 31),
        vline(140, 0, 31),
        hline(0, 25, W),
        hline(0, 31, W),
        pair(77, 1, 61, "Dis Amt:", "order.sot_total_discount"),
        pair(77, 6, 61, "Other Charges:", "COALESCE(order.sot_other_charges, 0)"),
        pair(142, 1, 50, "GROSS", "order.sot_sub_total"),
        pair(142, 6, 50, "TAX AMOUNT", "order.sot_total_gst"),
        pair(142, 11, 50, "DISC AMOUNT", "order.sot_total_discount"),
        pair(142, 16, 50, "ROUND OFF", "order.round_off"),
        text(5, 26, 68, 5, "FOR: {{ store.company }}"),
        field(77, 26, 61, 5, "order.sot_total_amount", "words:after", { fontSize: 7 }),
        pair(142, 26, 50, "GRAND TOTAL", "order.sot_total_amount", "money", { bold: true }),
        text(2, 32, 94, 11, "**Remarks:**\n{{ REPLACE(COALESCE(store.company_remarks, \"-\"), \"-\", \"\n\") }}"),
        vline(97, 31, 12, GREY),
        text(99, 32, 93, 11, "**Return Policy:**\n{{ REPLACE(COALESCE(store.company_return_policy, \"-\"), \"-\", \"\n\") }}"),
        hline(0, 43, W, GREY),
        text(2, 44, 190, 9, "**Terms & Conditions:**\n{{ REPLACE(COALESCE(store.company_terms_conditions, \"-\"), \"-\", \"\n\") }}"),
        text(120, 55, 72, 5, "Pharmacist Signature", { align: "right", valign: "bottom" }),
      ]),
    ]
  );
}

// 3" (80 mm) roll receipt, similar to the desktop thermal bill.
export function billingThermal80() {
  const w = 72; // 80 mm roll minus 4 mm margins each side (printable width)
  return preset(
    "billing_invoice",
    'Billing Receipt (Thermal 3")',
    thermal("T80", 80),
    [
      fixedBand("Header", 33, [
        text(0, 0, w, 5, "{{ store.company }}", { bold: true, fontSize: 10, align: "center" }),
        text(0, 5, w, 8, "{{ store.company_address }}, {{ store.company_city }}", { align: "center", fontSize: 7 }),
        text(0, 13, w, 4, "Ph: {{ store.company_telephone1 }}  GSTIN: {{ store.company_gstn }}", { align: "center", fontSize: 7 }),
        hline(0, 18, w, { lineStyle: "dashed" }),
        text(0, 18.5, w, 4, "TAX INVOICE", { bold: true, align: "center" }),
        text(0, 22.5, 40, 4, "Bill: {{ invoice_number }}", { fontSize: 7 }),
        text(w - 34, 22.5, 34, 4, "{{ created_date | date:'DD/MM/YYYY' }}", { fontSize: 7, align: "right" }),
        text(0, 26.5, w, 4, '{{ TRIM(cmr_first_name & " " & cmr_last_name) }}  {{ cmr_phone_number }}', { fontSize: 7 }),
        hline(0, 32, w, { lineStyle: "dashed" }),
      ]),
      tableBand(
        "Items",
        table(
          "items",
          "items",
          0,
          w,
          [
            col("name", "Item", 'item_name & IF(item_batch_number, "\n" & item_batch_number & "  Exp " & DATE(item_exp_date, "MM/YY"), "")', 40),
            col("qty", "Qty", "item_quantity", 10, { align: "right" }),
            col("rate", "Rate", "item_unit_price", 14, { align: "right", format: "fixed:2" }),
            col("amount", "Amt", "item_total_amount", 16, { align: "right", format: "fixed:2", footer: "SUM(col.amount)" }),
          ],
          {
            showFooter: false,
            style: { fontSize: 7.5, grid: "header", borderWidth: 0.5, borderColor: "#000000", cellPadding: 0.6 },
            headerStyle: { bold: true },
          }
        )
      ),
      fixedBand("Totals", 44, [
        hline(0, 0.5, w, { lineStyle: "dashed" }),
        pair(0, 1, w, "Items / Qty", 'COUNT() & " / " & SUM(col.qty)', ""),
        pair(0, 5.5, w, "Sub Total", "order.sot_sub_total"),
        pair(0, 10, w, "Discount", "order.sot_total_discount"),
        pair(0, 14.5, w, "GST", "order.sot_total_gst"),
        pair(0, 19, w, "Round Off", "order.round_off"),
        hline(0, 24, w),
        pair(0, 24.5, w, "NET AMOUNT", "order.sot_total_amount", "currency", { bold: true, fontSize: 10 }),
        field(0, 30, w, 4, 'JOIN(payments.payment_mode_name & ": " & FORMAT(payments.payment_amount, "money"), "  |  ")', "", { fontSize: 7, align: "center" }),
        text(0, 35, w, 8, "{{ store.company_remarks }}\nGoods once sold will not be taken back.", { fontSize: 7, align: "center" }),
      ]),
      fixedBand("UPI QR", 26, [qr((w - 24) / 2, 1, 24, '"upi://pay?pa=&pn=" & store.company & "&am=" & order.sot_total_amount & "&cu=INR"')], {
        visibleIf: "false",
        name: "UPI QR (enable and set your UPI id)",
      }),
    ]
  );
}
